/** @jsxImportSource preact */
import { h } from 'preact';
import { Modal } from './Modal';
import { LIFECYCLE_STAGES, TECHNICAL_SCOPE_TERMS } from '@/data/chartConfigs';
import {
  readDocumentFields,
  coverageDefinition,
  OECD_FRAMEWORK_URL,
  type ActorGroup,
  type CoverageGroups,
} from '@/lib/documentDetail';

export interface DocumentDetail {
  [key: string]: string;
}

interface DocumentDetailContentProps {
  documentId: string;
  document: DocumentDetail | null;
  loading: boolean;
  /** Omit the title, for a host page that already shows it as its own
   * heading (the per-document pages). */
  hideTitle?: boolean;
}

interface DocumentDetailModalProps extends DocumentDetailContentProps {
  isOpen: boolean;
  onClose: () => void;
}

// Field reading, coverage grouping and the subdomain names: lib/documentDetail
// (shared with the Webflow page builder).

// ─── Coverage helpers ────────────────────────────────────────────────────────

const BAR_COLOR = '#B10F41';
const TAG_GOOD_BG = '#41454D';
const TAG_GOOD_TEXT = '#ffffff';
const TAG_MINIMAL_BG = BAR_COLOR;
const TAG_MINIMAL_TEXT = '#ffffff';
const TAG_NEUTRAL_BG = '#e8e8ee';
const TAG_NEUTRAL_TEXT = '#41454D';

// ─── Shared components ──────────────────────────────────────────────────────

function Tag({ label, bg, color, tooltip }: { label: string; bg: string; color: string; tooltip?: string }) {
  return (
    <span
      title={tooltip}
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        margin: '3px 4px 3px 0',
        borderRadius: 4,
        fontSize: 12,
        lineHeight: 1.4,
        background: bg,
        color,
        cursor: tooltip ? 'default' : undefined,
      }}
    >
      {label}
    </span>
  );
}

function CoverageTagSection({
  title,
  groups,
  noCoverageLabel,
  subjectLabel,
}: {
  title: string;
  groups: CoverageGroups;
  noCoverageLabel: string;
  subjectLabel: string;
}) {
  const defStyle = { fontSize: 11, fontWeight: 400 as const, color: 'var(--gc-text-muted, #9ca3af)' };

  return (
    <div>
      <SectionHeading>{title}</SectionHeading>

      {groups.good.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gc-text-muted, #6b7280)', marginBottom: 6 }}>
            Good coverage
            {' '}<span style={defStyle}>
              — {coverageDefinition('good', subjectLabel)}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {groups.good.map((label) => <Tag key={label} label={label} bg={TAG_GOOD_BG} color={TAG_GOOD_TEXT} />)}
          </div>
        </div>
      )}

      {groups.minimal.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gc-text-muted, #6b7280)', marginBottom: 6 }}>
            Minimal coverage
            {' '}<span style={defStyle}>
              — {coverageDefinition('minimal', subjectLabel)}
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {groups.minimal.map((label) => <Tag key={label} label={label} bg={TAG_MINIMAL_BG} color={TAG_MINIMAL_TEXT} />)}
          </div>
        </div>
      )}

      <div style={{ marginTop: 4, fontSize: 11, color: 'var(--gc-text-muted, #9ca3af)', fontStyle: 'italic' }}>
        {noCoverageLabel}
      </div>
    </div>
  );
}

function SectionHeading({ children }: { children: preact.ComponentChildren }) {
  return (
    <h3 style={{ margin: '0 0 12px 0', fontSize: 15, fontWeight: 600, color: 'var(--gc-foreground, inherit)' }}>
      {children}
    </h3>
  );
}

function MetadataRow({ label, children }: { label: string; children: preact.ComponentChildren }) {
  return (
    <div style={{ display: 'flex', padding: '6px 0', borderBottom: '1px solid var(--gc-border, #e5e7eb)', fontSize: 13 }}>
      <div style={{ width: '35%', fontWeight: 600, color: 'var(--gc-text-muted, #6b7280)', flexShrink: 0 }}>{label}</div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

/** Two-column metadata grid row */
function TwoColRow({
  leftLabel, leftValue, rightLabel, rightValue,
}: {
  leftLabel: string; leftValue: string;
  rightLabel: string; rightValue: string;
}) {
  const cellLabel = {
    fontSize: 12,
    fontWeight: 600 as const,
    color: 'var(--gc-text-muted, #6b7280)',
    marginBottom: 2,
  };
  const cellValue = {
    fontSize: 13,
    color: 'var(--gc-foreground, inherit)',
    minHeight: 20,
  };

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: '1fr 1fr',
      gap: 24,
      padding: '8px 0',
      borderBottom: '1px solid var(--gc-border, #e5e7eb)',
    }}>
      <div>
        <div style={cellLabel}>{leftLabel}</div>
        <div style={cellValue}>{leftValue || '—'}</div>
      </div>
      <div>
        <div style={cellLabel}>{rightLabel}</div>
        <div style={cellValue}>{rightValue || '—'}</div>
      </div>
    </div>
  );
}

/** Section divider with optional top margin */
function SectionDivider() {
  return <div style={{ borderTop: '2px solid var(--gc-border, #e5e7eb)', margin: '8px 0 0 0' }} />;
}

// ─── Actors section ─────────────────────────────────────────────────────────

function ActorsSection({ groups }: { groups: ActorGroup[] }) {
  const hasAny = groups.some((g) => g.types.length > 0 || g.entities || g.summary);
  if (!hasAny) return null;

  return (
    <div>
      <SectionHeading>Actors</SectionHeading>

      {/* Column headers */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 16,
        paddingBottom: 6,
        borderBottom: '2px solid var(--gc-border, #e5e7eb)',
        marginBottom: 4,
      }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gc-text-muted, #6b7280)' }}>Entity Types</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gc-text-muted, #6b7280)' }}>Entities</div>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gc-text-muted, #6b7280)' }}>Summary</div>
      </div>

      {groups.map((group) => {
        const hasContent = group.types.length > 0 || group.entities || group.summary;
        if (!hasContent) return null;

        return (
          <div key={group.label}>
            {/* Sub-heading */}
            <div style={{
              fontSize: 13,
              fontWeight: 600,
              color: 'var(--gc-foreground, inherit)',
              padding: '10px 0 6px 0',
            }}>
              {group.label}
            </div>

            {/* 3-column grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 16,
              paddingBottom: 10,
              borderBottom: '1px solid var(--gc-border, #e5e7eb)',
            }}>
              {/* Entity Types (tags) */}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start' }}>
                {group.types.length > 0
                  ? group.types.map((t) => <Tag key={t} label={t} bg={TAG_MINIMAL_BG} color={TAG_MINIMAL_TEXT} />)
                  : <span style={{ fontSize: 12, color: 'var(--gc-text-muted, #9ca3af)' }}>—</span>
                }
              </div>

              {/* Entities */}
              <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--gc-foreground, inherit)' }}>
                {group.entities || '—'}
              </div>

              {/* Summary */}
              <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--gc-foreground, inherit)' }}>
                {group.summary || '—'}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function DocumentDetailModal({ isOpen, onClose, ...content }: DocumentDetailModalProps) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} width="min(1000px, 92vw)">
      <DocumentDetailContent {...content} />
    </Modal>
  );
}

/**
 * The document detail body, shared by the modal (above) and the inline
 * per-document page embed (DocumentDetailInline): one layout, two hosts.
 */
export function DocumentDetailContent({
  documentId,
  document,
  loading,
  hideTitle = false,
}: DocumentDetailContentProps) {
  const {
    title, shortSummary, linkToDoc, jurisdiction, authority, parentAuthority, collections,
    recentActivity, recentActivityDate, legislativeStatus, legislativeStatusSummary,
    actorGroups, lifecycleStages, aiLifecycleSummary, technicalScope, technicalScopeSummary,
    riskCoverageSummary, sectorsSummary, subdomainGroups, sectorGroups,
  } = readDocumentFields(document);
  const agoraUrl = documentId ? `https://agora.eto.tech/instrument/${documentId}` : '';

  return (
    <>
      {loading ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--gc-text-muted, #6b7280)' }}>
          Loading document details...
        </div>
      ) : !document ? (
        <div style={{ padding: 48, textAlign: 'center', color: 'var(--gc-text-muted, #6b7280)' }}>
          Document not found.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {/* Title */}
          {!hideTitle && (
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, lineHeight: 1.3, color: 'var(--gc-foreground, inherit)' }}>
                {title || documentId}
              </h2>
            </div>
          )}

          {/* Summary */}
          {shortSummary && (
            <div style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--gc-foreground, inherit)' }}>
              {shortSummary}
            </div>
          )}

          {/* Links */}
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
            {agoraUrl && (
              <a
                href={agoraUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: 'var(--gc-link, inherit)',
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '6px 14px',
                  border: '1px solid currentColor',
                  borderRadius: 4,
                  display: 'inline-block',
                }}
              >
                AGORA link ↗
              </a>
            )}
            {linkToDoc && (
              <a
                href={linkToDoc}
                target="_blank"
                rel="noreferrer"
                style={{
                  color: 'var(--gc-link, inherit)',
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '6px 14px',
                  border: '1px solid currentColor',
                  borderRadius: 4,
                  display: 'inline-block',
                }}
              >
                Link to document ↗
              </a>
            )}
          </div>

          {/* Metadata: 2-column layout */}
          <div style={{ borderTop: '1px solid var(--gc-border, #e5e7eb)' }}>
            <TwoColRow
              leftLabel="Authority" leftValue={authority}
              rightLabel="Parent authority" rightValue={parentAuthority}
            />
            <TwoColRow
              leftLabel="Jurisdiction" leftValue={jurisdiction}
              rightLabel="Collections" rightValue={collections}
            />
            <TwoColRow
              leftLabel="Most recent activity" leftValue={recentActivity}
              rightLabel="Most recent activity date" rightValue={recentActivityDate}
            />
          </div>

          {/* Risk subdomain coverage */}
          <CoverageTagSection
            title="Risk subdomain coverage"
            groups={subdomainGroups}
            noCoverageLabel="All other risk subdomains had no coverage in this document."
            subjectLabel="risk subdomain"
          />
          {riskCoverageSummary && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gc-text-muted, #6b7280)', marginBottom: 4 }}>
                Risk Coverage Summary
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--gc-foreground, inherit)' }}>
                {riskCoverageSummary}
              </div>
            </div>
          )}

          {/* Sector coverage */}
          <CoverageTagSection
            title="Sector coverage"
            groups={sectorGroups}
            noCoverageLabel="All other sectors had no coverage in this document."
            subjectLabel="sector"
          />
          {sectorsSummary && (
            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gc-text-muted, #6b7280)', marginBottom: 4 }}>
                Sectors Summary
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--gc-foreground, inherit)' }}>
                {sectorsSummary}
              </div>
            </div>
          )}

          {/* Legislative Status */}
          <SectionDivider />
          <div style={{ borderTop: '1px solid var(--gc-border, #e5e7eb)' }}>
            <TwoColRow
              leftLabel="Legislative Status" leftValue={legislativeStatus}
              rightLabel="Legislative Status Summary" rightValue={legislativeStatusSummary}
            />
          </div>

          {/* Actors */}
          <SectionDivider />
          <ActorsSection groups={actorGroups} />

          {/* AI Lifecycle Stages */}
          <SectionDivider />
          <div>
            <SectionHeading>AI Lifecycle Stages</SectionHeading>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--gc-foreground, inherit)', marginBottom: 12 }}>
              Lifecycle stages covered by the documents, as defined in the{' '}
              <a
                href={OECD_FRAMEWORK_URL}
                target="_blank"
                rel="noreferrer"
                style={{ color: 'var(--gc-link, inherit)', textDecoration: 'underline' }}
              >
                OECD Framework for the Classification of AI Systems
              </a>
            </div>

            {lifecycleStages.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 12 }}>
                {LIFECYCLE_STAGES.map((stage) => {
                  const active = lifecycleStages.includes(stage);
                  return (
                    <Tag
                      key={stage}
                      label={stage}
                      bg={active ? TAG_MINIMAL_BG : TAG_NEUTRAL_BG}
                      color={active ? TAG_MINIMAL_TEXT : TAG_NEUTRAL_TEXT}
                      tooltip={active ? 'Covered by document' : 'Not covered by document'}
                    />
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--gc-text-muted, #9ca3af)', fontStyle: 'italic', marginBottom: 12 }}>
                No lifecycle stages identified.
              </div>
            )}

            {aiLifecycleSummary && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gc-text-muted, #6b7280)', marginBottom: 4 }}>
                  AI Lifecycle Summary
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--gc-foreground, inherit)' }}>
                  {aiLifecycleSummary}
                </div>
              </div>
            )}
          </div>

          {/* Technical Scope */}
          <SectionDivider />
          <div>
            <SectionHeading>Technical Scope</SectionHeading>
            <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--gc-foreground, inherit)', marginBottom: 12 }}>
              Terms relating to technical scope that are mentioned or implicitly referenced in the document
            </div>

            {technicalScope.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: 12 }}>
                {TECHNICAL_SCOPE_TERMS.map((term) => {
                  const active = technicalScope.includes(term);
                  return (
                    <Tag
                      key={term}
                      label={term}
                      bg={active ? TAG_MINIMAL_BG : TAG_NEUTRAL_BG}
                      color={active ? TAG_MINIMAL_TEXT : TAG_NEUTRAL_TEXT}
                      tooltip={active ? 'Mentioned in document' : 'Not mentioned in document'}
                    />
                  );
                })}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: 'var(--gc-text-muted, #9ca3af)', fontStyle: 'italic', marginBottom: 12 }}>
                No technical scope terms identified.
              </div>
            )}

            {technicalScopeSummary && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gc-text-muted, #6b7280)', marginBottom: 4 }}>
                  Technical Scope Summary
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--gc-foreground, inherit)' }}>
                  {technicalScopeSummary}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
